/**
 * Canvas drawing utilities for calibration point markers
 */

import { calculateDistanceBetweenPoints } from "../../../../../../../utils/calibrationPointCalculations";
import { PendingCalibrationPoint } from "../useSetupWizardStore";

const MARKER_RADIUS = 12;
const LABEL_FONT_SIZE = 10;
const COLORS = {
  pending: "#f59e0b", // warning/amber
  complete: "#22c55e", // success/green
  ring: "rgba(255, 255, 255, 0.9)",
  shadow: "rgba(0, 0, 0, 0.3)",
  line: "rgba(59, 130, 246, 0.8)", // blue
  lineShadow: "rgba(0, 0, 0, 0.4)",
};

/**
 * Draw connecting lines between calibration points with distance labels
 */
export function drawCalibrationLines(
  ctx: CanvasRenderingContext2D,
  points: PendingCalibrationPoint[],
  canvasWidth: number,
  canvasHeight: number
): void {
  // Only draw lines between points that have GPS coordinates
  const completedPoints = points.filter(
    (p) => p.latitude !== undefined && p.longitude !== undefined
  );

  if (completedPoints.length < 2) return;

  ctx.save();

  // Draw lines between consecutive points
  for (let i = 0; i < completedPoints.length; i++) {
    for (let j = i + 1; j < completedPoints.length; j++) {
      const p1 = completedPoints[i];
      const p2 = completedPoints[j];

      const x1 = p1.x * canvasWidth;
      const y1 = p1.y * canvasHeight;
      const x2 = p2.x * canvasWidth;
      const y2 = p2.y * canvasHeight;

      // Calculate GPS distance
      const distance = calculateDistanceBetweenPoints(
        { x: p1.x, y: p1.y, latitude: p1.latitude!, longitude: p1.longitude! },
        { x: p2.x, y: p2.y, latitude: p2.latitude!, longitude: p2.longitude! }
      );

      // Draw line shadow
      ctx.beginPath();
      ctx.moveTo(x1 + 1, y1 + 1);
      ctx.lineTo(x2 + 1, y2 + 1);
      ctx.strokeStyle = COLORS.lineShadow;
      ctx.lineWidth = 3;
      ctx.stroke();

      // Draw line
      ctx.beginPath();
      ctx.moveTo(x1, y1);
      ctx.lineTo(x2, y2);
      ctx.strokeStyle = COLORS.line;
      ctx.lineWidth = 2;
      ctx.setLineDash([6, 4]);
      ctx.stroke();
      ctx.setLineDash([]);

      // Draw distance label at midpoint
      const midX = (x1 + x2) / 2;
      const midY = (y1 + y2) / 2;
      const distanceText = `${Math.round(distance)} ft`;

      // Background pill for label
      ctx.font = "bold 11px sans-serif";
      const textWidth = ctx.measureText(distanceText).width;
      const pillWidth = textWidth + 12;
      const pillHeight = 18;

      ctx.beginPath();
      ctx.roundRect(
        midX - pillWidth / 2,
        midY - pillHeight / 2,
        pillWidth,
        pillHeight,
        4
      );
      ctx.fillStyle = "rgba(0, 0, 0, 0.75)";
      ctx.fill();

      // Label text
      ctx.fillStyle = "white";
      ctx.textAlign = "center";
      ctx.textBaseline = "middle";
      ctx.fillText(distanceText, midX, midY);
    }
  }

  ctx.restore();
}

/**
 * Draw a single calibration point marker on the canvas
 */
export function drawCalibrationPointMarker(
  ctx: CanvasRenderingContext2D,
  point: PendingCalibrationPoint,
  canvasWidth: number,
  canvasHeight: number,
  isHovered: boolean = false
): void {
  const hasGPS =
    point.latitude !== undefined && point.longitude !== undefined;

  // Convert relative coordinates (0-1) to canvas coordinates
  const x = point.x * canvasWidth;
  const y = point.y * canvasHeight;

  // Scale up when hovered for visual feedback
  const scale = isHovered ? 1.25 : 1.0;
  const radius = MARKER_RADIUS * scale;

  ctx.save();

  // Draw hover glow effect
  if (isHovered) {
    ctx.beginPath();
    ctx.arc(x, y, radius + 5, 0, Math.PI * 2);
    ctx.fillStyle = "rgba(59, 130, 246, 0.35)"; // blue glow
    ctx.fill();
  }

  // Draw shadow
  ctx.beginPath();
  ctx.arc(x + 2, y + 2, radius, 0, Math.PI * 2);
  ctx.fillStyle = COLORS.shadow;
  ctx.fill();

  // Draw outer ring
  ctx.beginPath();
  ctx.arc(x, y, radius, 0, Math.PI * 2);
  ctx.fillStyle = COLORS.ring;
  ctx.fill();

  // Draw inner circle
  ctx.beginPath();
  ctx.arc(x, y, radius - 3, 0, Math.PI * 2);
  ctx.fillStyle = hasGPS ? COLORS.complete : COLORS.pending;
  ctx.fill();

  // Draw label
  const fontSize = LABEL_FONT_SIZE * scale;
  ctx.font = `bold ${fontSize}px sans-serif`;
  ctx.textAlign = "center";
  ctx.textBaseline = "middle";
  ctx.fillStyle = "white";
  ctx.fillText(point.label, x, y);

  ctx.restore();
}

/**
 * Draw all calibration point markers and connecting lines on the canvas
 */
export function drawCalibrationPointMarkers(
  ctx: CanvasRenderingContext2D,
  points: PendingCalibrationPoint[],
  canvasWidth: number,
  canvasHeight: number,
  hoveredIndex?: number
): void {
  // Draw lines first (behind markers)
  drawCalibrationLines(ctx, points, canvasWidth, canvasHeight);

  // Draw markers on top
  points.forEach((point, index) => {
    drawCalibrationPointMarker(ctx, point, canvasWidth, canvasHeight, index === hoveredIndex);
  });
}

/**
 * Check if a click position is near a calibration point marker
 * Returns the index of the point if found, -1 otherwise
 */
export function hitTestCalibrationPoint(
  points: PendingCalibrationPoint[],
  clickX: number,
  clickY: number,
  canvasWidth: number,
  canvasHeight: number
): number {
  const hitRadius = MARKER_RADIUS + 5;

  for (let i = 0; i < points.length; i++) {
    const point = points[i];
    const x = point.x * canvasWidth;
    const y = point.y * canvasHeight;

    const distance = Math.sqrt(
      Math.pow(clickX - x, 2) + Math.pow(clickY - y, 2)
    );

    if (distance <= hitRadius) {
      return i;
    }
  }

  return -1;
}
